import React, { useEffect } from 'react';
import { useLazyGetCaptchaQuery } from '@/store/api/authApi';
import { useLocation } from 'react-router-dom';

interface CaptchaBoxProps {
  onCaptchaChange: (id: string) => void;
  refreshTrigger?: number;
  autoFetch?: boolean;
}

const CaptchaBox: React.FC<CaptchaBoxProps> = ({
  onCaptchaChange,
  refreshTrigger,
  autoFetch = true,
}) => {
  const { pathname } = useLocation();

  const [getCaptcha, { data, isFetching, isError }] = useLazyGetCaptchaQuery();

  // Initial fetch
  useEffect(() => {
    if (autoFetch) {
      getCaptcha();
    }
  }, [autoFetch, getCaptcha]);

  // Refresh captcha before expiry on login page
  useEffect(() => {
    if (pathname !== '/login') return;

    const intervalId = window.setInterval(() => {
      getCaptcha();
    }, 115000);

    return () => window.clearInterval(intervalId);
  }, [pathname, getCaptcha]);

  // Manual refresh
  useEffect(() => {
    if (refreshTrigger === undefined) return;

    getCaptcha();
  }, [refreshTrigger, getCaptcha]);

  // Send captcha ID to parent
  useEffect(() => {
    if (data?.captchaId) {
      onCaptchaChange(data.captchaId);
    }
  }, [data?.captchaId, onCaptchaChange]);

  return (
    <div className="flex h-[3.125rem] w-[9.375rem] items-center justify-center">
      {isFetching ? (
        <p className="text-xs text-gray-400">Loading...</p>
      ) : data?.image ? (
        <img
          src={data.image}
          alt="Captcha"
          className="h-full w-full object-contain"
          draggable={false}
        />
      ) : (
        <p className="text-xs text-gray-400">
          {isError ? 'Unable to load' : 'Captcha unavailable'}
        </p>
      )}
    </div>
  );
};

export default CaptchaBox;
